import InputTextBox from "Components/common/InputTextBox";
import { observer } from "mobx-react";
import SingleSelectDropdown from "Pages/Dashboard/settings/Calendar/Pages/Events/CreateEvent/Components/SingleSelectDropdown";
import React, { useEffect, useState } from "react";
import { conditionsOperatorObject } from "Static/constant";
import { settingsStoreData } from "Stores/settingsData";
import userDetail from "Stores/userDetail";
import Utils from "Utils/utils";
import ActionButton from "./ActionButton";
import DropdownButton from "./DropdownButton";
import {
  booleanOptions,
  liConnectionReqOptions,
  liMsgOptions,
  textOperatorList
} from "./FilterConstants";
import SingleDropdown from "./SingleDropdown";

export const ContactMetricsFilter = observer(props => {
  const {
    label = "",
    keyValue = "",
    activeFilter = "",
    data = [],
    errMsg = "",
    setErrMsg = () => { },
    setActiveFilter = () => { },
    setAction = () => { },
    selectedFilterCbk = () => { },
    customConditionFieldnameList = [],
    singleFieldWithoutDropdown = false,
    // Render field + operator on row 1, value on row 2 (opt-in; sequence filter).
    inlineOperator = false
  } = props || {};

  let toggleDropdown = activeFilter === keyValue;

  const [activeDropdownIndex, setActiveDropdownIndex] = useState(false);
  const [fieldNameObject, setFieldNameObject] = useState({});
  const disabledFeatureSet = new Set(userDetail?.featuresDisabled || []);
  // Per-row in-progress value — see ContactFieldFilter; keeps keystrokes local in
  // the auto-apply (sequence) panel so each one doesn't trigger a re-query.
  const [valueDrafts, setValueDrafts] = useState({});

  const clearDraft = index => {
    setValueDrafts(prev => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const getConditionFieldsObject = () => {
    let tempObj = {};
    let tempArr = customConditionFieldnameList?.length
      ? customConditionFieldnameList
      : conditionFieldnameList;
    tempArr?.length > 0 &&
      [...tempArr].map(item => {
        tempObj[item?.key] = item?.value;
      });
    setFieldNameObject({ ...fieldNameObject, ...tempObj });
  };

  const validateFieldNameAlreadyExist = (newFieldName = "") => {
    let count = 0;
    if (data?.length > 1) {
      data.some(element => {
        if (element?.fieldName === newFieldName) {
          count++;
        }
      });
    }
    return !count > 0;
  };

  const handleAction = (refId = "", item = "", index = "") => {
    if (refId === "fieldName") {
      if (!validateFieldNameAlreadyExist(item?.key)) {
        return;
      }
      selectedFilterCbk("add", keyValue);
    }
    errMsg && setErrMsg();
    const { key = "", subKey = "", value = "" } = item || {};
    let tempData = [...data];
    tempData[index]["value"] = [
      "profileviewscount",
      "hasvisitedwebsite"
    ].includes(item?.key?.toLowerCase())
      ? "true"
      : ["linkedinconnectionrequest", "linkedinmessage"].includes(
          item?.key?.toLowerCase()
        )
      ? "Sent"
      : "";
    tempData[index][refId] = refId === "fieldName" ? key : subKey || "";
    tempData[index]["label"] = value || "";
    setActiveDropdownIndex(false);
    setAction(keyValue, tempData);
    clearDraft(index);
  };

  const setFilterData = (tempData = {}, value = "", index = "") => {
    if (keyValue?.toLowerCase() === "contacts") {
      tempData[index]["fieldName"] = "contactsCount";
      tempData[index]["label"] = "Number of Contacts";
    }
    tempData[index]["value"] = value || "";
    return tempData;
  };

  const handleInputAction = (id = "", value = "", index = "") => {
    errMsg && setErrMsg();
    let tempData = [...data];
    setAction(keyValue, setFilterData(tempData, value, index));
  };

  // Sequence panel: keep typed value local until committed (Enter / blur / → btn).
  const handleDraftChange = (id = "", value = "", index = "") => {
    errMsg && setErrMsg();
    setValueDrafts(prev => ({ ...prev, [index]: value }));
  };

  const commitValueDraft = (index = "") => {
    const draft = valueDrafts?.[index];
    if (draft === undefined) return;
    errMsg && setErrMsg();
    let tempData = [...data];
    setAction(keyValue, setFilterData(tempData, draft, index));
    clearDraft(index);
  };

  // ──────────────── Chip model (mirrors ContactFieldFilter) ────────────────
  const [showBuilder, setShowBuilder] = useState(false);
  const OP_SYMBOL = { EQUALS: "=", GREATER_THAN: ">", LESS_THAN: "<" };
  const isBoolField = fn =>
    [
      "profileviewscount",
      "linkedinmessage",
      "linkedinconnectionrequest",
      "hasvisitedwebsite",
      "whatsappmessage"
    ].includes((fn || "").toLowerCase());

  const chipRows = (data || []).filter(r => r?.committed === true);
  const builderIndex = (data || []).findIndex(r => r?.committed !== true);

  const chipLabel = item => {
    const f = fieldNameObject?.[item?.fieldName] || item?.fieldName || "";
    if (isBoolField(item?.fieldName)) return `${f}: ${item?.value || ""}`;
    const op = OP_SYMBOL[item?.operator] || "";
    return `${f} ${op} ${item?.value || ""}`;
  };

  const builderReady = index => {
    const row = (data || [])[index] || {};
    if (!row.fieldName || !row.operator) return false;
    const v = valueDrafts?.[index] !== undefined ? valueDrafts[index] : row.value;
    return v !== "" && v != null;
  };

  const removeChip = dataIdx => {
    const rows = [...(data || [])];
    rows.splice(dataIdx, 1);
    if (!rows.some(r => r?.committed === true)) selectedFilterCbk("remove", keyValue);
    setAction(keyValue, rows);
    setValueDrafts({});
    errMsg && setErrMsg();
  };

  const commitBuilder = (index = 0) => {
    const rows = [...(data || [])];
    const row = rows[index] || {};
    if (!row.fieldName || !row.operator) return;
    const draftVal = valueDrafts?.[index];
    const value = draftVal !== undefined ? draftVal : row.value || "";
    if (value === "" || value == null) return;
    rows[index] = { ...row, value, committed: true };
    rows.push({ fieldName: "", operator: "EQUALS", value: "", committed: false });
    selectedFilterCbk("add", keyValue);
    setAction(keyValue, rows);
    clearDraft(index);
    setShowBuilder(false);
    errMsg && setErrMsg();
  };

  // One-time normalize: complete rows → committed chips + one builder.
  useEffect(() => {
    if (!inlineOperator || singleFieldWithoutDropdown) return;
    const rows = data || [];
    const committedRows = rows
      .filter(r => r?.fieldName && r?.operator && r.value !== "" && r.value != null)
      .map(r => ({ ...r, committed: true }));
    const next = [
      ...committedRows,
      { fieldName: "", operator: "EQUALS", value: "", committed: false }
    ];
    if (JSON.stringify(next) !== JSON.stringify(rows)) setAction(keyValue, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCustomOperatorList = (fieldName = "") => {
    if (fieldName?.toLowerCase() === "websitevisitedurl") {
      return [...textOperatorList].map(item => ({
        ...item,
        method: handleAction
      }));
    } else if (
      [
        "profileviewscount",
        "linkedinmessage",
        "linkedinconnectionrequest",
        "hasvisitedwebsite"
      ]?.includes(fieldName?.toLowerCase())
    ) {
      return operatorList?.slice(0, 1);
    } else {
      return operatorList;
    }
  };

  const operatorList = [
    {
      value: "<span class='conditionSymbol'>&equals;</span><span class='conditionLabel'>Equals</span>",
      subKey: "EQUALS",
      key: "equalCond",
      method: handleAction
    },
    {
      value: "<span class='conditionSymbol'>&gt;</span><span class='conditionLabel'>Greater than</span>",
      subKey: "GREATER_THAN",
      key: "greaterCond",
      method: handleAction
    },
    {
      value: "<span class='conditionSymbol'>&lt;</span><span class='conditionLabel'>Less than</span>",
      subKey: "LESS_THAN",
      key: "lesserCond",
      method: handleAction
    }
  ];

  const conditionFieldnameList = [
    {
      value: "Reply Count",
      subKey: "REPLIED",
      key: "replyCount",
      method: handleAction
    },
    {
      value: "Open Count",
      subKey: "OPENED",
      key: "openCount",
      method: handleAction
    },
    {
      value: "Click Count",
      subKey: "CLICKED",
      key: "clickCount",
      method: handleAction
    },
    {
      value: "Sent Count",
      subKey: "SENT",
      key: "sentCount",
      method: handleAction
    },
    {
      value: "Calls Made",
      subKey: "CALLMADE",
      key: "callsMade",
      method: handleAction
    },
    {
      value: "Calls Logged",
      subKey: "CALLLOGGED",
      key: "callsLogged",
      method: handleAction
    },
    {
      value: "Website Visit Count",
      subKey: "WEBSITEVISITEDCOUNT",
      key: "seqWebsiteVisitedCount",
      method: handleAction,
      featureTitle: "WEBSITE_TRACKING"
    },
    {
      value: "Has Visited Website",
      subKey: "HASVISITEDWEBSITE",
      key: "hasVisitedWebsite",
      method: handleAction,
      featureTitle: "WEBSITE_TRACKING"
    },
    {
      value: "Website Visit URL",
      subKey: "WEBSITEVISITEDURL",
      key: "seqWebsiteVisitedUrl",
      method: handleAction,
      featureTitle: "WEBSITE_TRACKING"
    },
    {
      value: "LinkedIn connection request",
      subKey: "LINKEDINCONNNECTIONREQUEST",
      key: "linkedInConnectionRequest",
      method: handleAction,
      featureTitle: "LINKEDIN_AUTOMATION"
    },
    {
      value: "LinkedIn message",
      subKey: "LINKEDINMESSAGE",
      key: "linkedInMessage",
      method: handleAction,
      featureTitle: "LINKEDIN_AUTOMATION"
    },
    {
      value: "LinkedIn profile viewed",
      subKey: "PROFILEVIEWSCOUNT",
      key: "profileviewscount",
      method: handleAction,
      featureTitle: "LINKEDIN_AUTOMATION"
    }
  ];

  const getConditionFieldnameList = () => {
    if (!customConditionFieldnameList?.length) {
      return conditionFieldnameList.filter(item => {
        if (item?.featureTitle) {
          return !disabledFeatureSet.has(item.featureTitle);
        }
        return true;
      });
    } else {
      return customConditionFieldnameList.map(item => ({
        ...item,
        method: handleAction
      }));
    }
  };

  const resetOrAddNewItem = (isPush = false) => {
    let newObj = {
      fieldName: "",
      operator: "EQUALS",
      value: ""
    };
    if (isPush) {
      return newObj;
    }
    selectedFilterCbk("remove", keyValue);
    setAction(keyValue, [newObj]);
  };

  const removeItem = index => {
    setValueDrafts({});
    let tempData = [...data];
    let len = tempData?.length;
    if (len) {
      if (len === 1) {
        resetOrAddNewItem();
      } else {
        tempData.splice(index, 1);
        tempData.filter(item => item?.fieldName)?.length < 1 &&
          selectedFilterCbk("remove", keyValue);
        setAction(keyValue, tempData);
      }
      errMsg && setErrMsg();
    }
  };

  const addItem = () => {
    let tempData = [...data];
    tempData.push(resetOrAddNewItem(true));
    errMsg && setErrMsg();
    setAction(keyValue, tempData);
  };

  const resetItem = () => {
    errMsg && setErrMsg();
    data?.filter(item => item?.fieldName)?.length && resetOrAddNewItem();
  };

  const dropdownOptions = {
    label: label,
    count: data?.filter(item => item?.fieldName)?.length || 0,
    keyValue: keyValue,
    toggleDropdown: toggleDropdown,
    callback: setActiveFilter
  };

  const getDropdownObj = fieldName => {
    return {
      name: `${fieldName} Dropdown`,
      id: fieldName,
      type: "select",
      options: ["hasvisitedwebsite", "profileviewscount"].includes(
        fieldName?.toLowerCase()
      )
        ? [...booleanOptions]
        : fieldName?.toLowerCase() === "linkedinconnectionrequest"
        ? [...liConnectionReqOptions]
        : [...liMsgOptions],
      text: `${fieldName} Dropdown`,
      showLabel: false
    };
  };

  const filterFn = Utils.getFilterFn("CONTACT_METRICS_FILTER");

  useEffect(() => {
    getConditionFieldsObject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsStoreData?.customFields]);

  return (
    <div className="conditionField">
      <DropdownButton {...dropdownOptions} />
      {toggleDropdown && (
        <div className="dropdownSearchBox">
          <span className="horizontalBorderLine" />
          {inlineOperator && !singleFieldWithoutDropdown && (
            <div className="filterChipWrap">
              {chipRows.length > 0 && (
                <div className="filterChips">
                  {chipRows.map((c, i) => (
                    <span className="filterChip" key={`mchip-${i}`}>
                      <span className="chipText">{chipLabel(c)}</span>
                      <i
                        className="material-icons chipRemove"
                        onClick={() => removeChip(data.indexOf(c))}
                      >
                        close
                      </i>
                    </span>
                  ))}
                </div>
              )}
              {!showBuilder && (
                <div
                  className="addNewButton chipAddBtn"
                  onClick={() => {
                    setShowBuilder(true);
                    setActiveDropdownIndex(`fieldName-${builderIndex}`);
                  }}
                >
                  <i className="material-icons addIcon">add_circle_outline</i>
                  <span className="text">Add condition</span>
                </div>
              )}
              {showBuilder &&
                builderIndex > -1 &&
                (() => {
                  const index = builderIndex;
                  const bItem = data[index] || {};
                  const bFieldName = bItem.fieldName || "";
                  const bOperator = bItem.operator || "EQUALS";
                  const bBool = isBoolField(bFieldName);
                  const bDraft =
                    valueDrafts?.[index] !== undefined
                      ? valueDrafts[index]
                      : bItem.value || "";
                  const bDropObj = getDropdownObj(bFieldName);
                  return (
                    <div className="chipBuilder">
                      <div className="fieldnameDropdown fieldnameOperatorRow">
                        <SingleDropdown
                          isDropdownAllowed={true}
                          options={getConditionFieldnameList()}
                          defaultValue={fieldNameObject?.[bFieldName] || ""}
                          label="Select field"
                          referenceId="fieldName"
                          parentIndex={index}
                          hasCustomFieldValue={true}
                          showDropdown={activeDropdownIndex === `fieldName-${index}`}
                          setActiveDropdownIndex={setActiveDropdownIndex}
                          search={true}
                          customDropdownClassName="bottomDropdown"
                          filterFn={filterFn}
                        />
                        {bFieldName && !bBool && (
                          <SingleDropdown
                            isDropdownAllowed={true}
                            options={getCustomOperatorList(bFieldName)}
                            defaultValue={conditionsOperatorObject?.[bOperator] || ""}
                            showDropdown={activeDropdownIndex === `operator-${index}`}
                            setActiveDropdownIndex={setActiveDropdownIndex}
                            parentIndex={index}
                            referenceId="operator"
                            className="inlineOperatorLabel"
                          />
                        )}
                      </div>
                      {bFieldName && (
                        <div className="fieldOperatorValue valueOnlyRow">
                          {!bBool ? (
                            <div className="valueWithApply">
                              <InputTextBox
                                name="number"
                                type="number"
                                value={bDraft}
                                change={handleDraftChange}
                                cbk={e => {
                                  if (e?.key === "Enter" || e?.keyCode === 13)
                                    commitBuilder(index);
                                }}
                                parentIndex={index}
                                placeHolder="Value"
                                autoFocus={true}
                              />
                              {builderReady(index) && (
                                <span
                                  className="applyValueLink"
                                  onClick={() => commitBuilder(index)}
                                >
                                  Apply
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="valueWithApply">
                              <SingleSelectDropdown
                                {...bDropObj}
                                change={(p1, p2) => handleDraftChange(p1, p2, index)}
                                value={bDraft || bDropObj?.options?.[0] || ""}
                                key={bFieldName}
                                displayName="displayName"
                                timezone={true}
                              />
                              <span
                                className="applyValueLink"
                                onClick={() => commitBuilder(index)}
                              >
                                Apply
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>
          )}
          {!(inlineOperator && !singleFieldWithoutDropdown) &&
            ((data?.length && (
            <div className="conditionalFieldCard">
              {data.map((item, index) => {
                const {
                  fieldName = "",
                  operator = "EQUALS",
                  value = ""
                } = item || {};

                const hasSingleDropdownWithBoolValue = [
                  "profileviewscount",
                  "linkedinmessage",
                  "linkedinconnectionrequest",
                  "hasvisitedwebsite"
                ].includes(fieldName?.toLowerCase());

                const conditionalFieldProps = {
                  isDropdownAllowed: true,
                  options: getConditionFieldnameList(),
                  defaultValue: fieldNameObject?.[fieldName] || "",
                  label: "Select field",
                  referenceId: "fieldName",
                  parentIndex: index,
                  hasCustomFieldValue: true,
                  showDropdown: activeDropdownIndex === `fieldName-${index}`,
                  setActiveDropdownIndex: setActiveDropdownIndex
                };

                // Inline layout only when there's a field dropdown row to host
                // the operator (not the single-field variant).
                const useInline = inlineOperator && !singleFieldWithoutDropdown;

                const fieldOperatorProps = {
                  isDropdownAllowed: true,
                  options: getCustomOperatorList(fieldName),
                  defaultValue: hasSingleDropdownWithBoolValue
                    ? conditionsOperatorObject?.["EQUALS"]
                    : conditionsOperatorObject?.[operator] || "",
                  showDropdown:
                    !hasSingleDropdownWithBoolValue &&
                    activeDropdownIndex === `operator-${index}`,
                  setActiveDropdownIndex: setActiveDropdownIndex,
                  parentIndex: index,
                  referenceId: "operator",
                  className: useInline ? "inlineOperatorLabel" : ""
                };

                const inputProps = {
                  name: "number",
                  type: hasSingleDropdownWithBoolValue ? "text" : "number",
                  value: value,
                  change: handleInputAction,
                  parentIndex: index,
                  placeHolder: "Value",
                  autoFocus: useInline
                };
                const dropDownObj = getDropdownObj(fieldName);

                return (
                  <div
                    className="conditionalField"
                    key={`conditionalField${index}`}
                  >
                    {!singleFieldWithoutDropdown && (
                      <div
                        className={`fieldnameDropdown ${
                          useInline ? "fieldnameOperatorRow" : ""
                        }`}
                      >
                        <SingleDropdown
                          {...conditionalFieldProps}
                          search={true}
                          customDropdownClassName="bottomDropdown"
                          filterFn={filterFn}
                        />
                        {useInline && fieldName && (
                          <SingleDropdown {...fieldOperatorProps} />
                        )}
                        <i
                          className="material-icons-outlined deleteIcon"
                          onClick={() => removeItem(index)}
                        >
                          delete
                        </i>
                      </div>
                    )}
                    {(fieldName || singleFieldWithoutDropdown) && (
                      <div
                        className={`fieldOperatorValue ${
                          useInline ? "valueOnlyRow" : ""
                        } ${!fieldName && !singleFieldWithoutDropdown
                            ? "disableInput"
                            : ""
                          }`}
                      >
                        {!useInline && <SingleDropdown {...fieldOperatorProps} />}
                        {!hasSingleDropdownWithBoolValue ? (
                          useInline ? (
                            <div className="valueWithApply">
                              <InputTextBox
                                name="number"
                                type="number"
                                value={
                                  valueDrafts?.[index] !== undefined
                                    ? valueDrafts[index]
                                    : value
                                }
                                change={handleDraftChange}
                                cbk={e => {
                                  if (e?.key === "Enter" || e?.keyCode === 13)
                                    commitValueDraft(index);
                                }}
                                onBlur={() => commitValueDraft(index)}
                                parentIndex={index}
                                placeHolder="Value"
                                autoFocus={true}
                              />
                              <i
                                className="material-icons applyValueBtn"
                                title="Apply"
                                onClick={() => commitValueDraft(index)}
                              >
                                arrow_forward
                              </i>
                            </div>
                          ) : (
                            <InputTextBox {...inputProps} />
                          )
                        ) : (
                          <SingleSelectDropdown
                            {...dropDownObj}
                            change={(param1, param2) => {
                              handleInputAction(param1, param2, index);
                            }}
                            value={value || dropDownObj?.options?.[0] || ""}
                            key={fieldName}
                            displayName="displayName"
                            timezone={true}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )) ||
              "")}
          {!(inlineOperator && !singleFieldWithoutDropdown) &&
            !singleFieldWithoutDropdown && (
              <ActionButton
                resetCbk={resetItem}
                addCbk={addItem}
                isReset={false}
                addLabel="Add"
              />
            )}
          {!(inlineOperator && !singleFieldWithoutDropdown) &&
            errMsg === keyValue && (
              <span className="errMessage">Please enter a value</span>
            )}
        </div>
      )}
    </div>
  );
});

export default ContactMetricsFilter;
